/**
 * The words of device builds (DeviceBuildDialog, DeviceBuildsList): providers, variants, what a build makes, statuses,
 * file kinds and durations, in plain English (no "AAB" without saying what it is for).
 */

export const PLATFORM_LABEL = { android: 'Android', ios: 'iOS' };

export const PROVIDER_LABEL = { flow: 'Flow (in your cluster)', eas: 'Expo EAS', codemagic: 'Codemagic', mac: 'My Mac' };
export const PROVIDER_SHORT = { flow: 'Flow', eas: 'Expo EAS', codemagic: 'Codemagic', mac: 'a Mac' };

/** Who built it, for the list: "Flow", "Codemagic", "Giulio's Mac". */
export const builtByOf = (build) => (build?.provider === 'mac' && build.runner?.name ? build.runner.name : PROVIDER_SHORT[build?.provider] || build?.provider || '');
export const PROVIDER_SITE = { eas: 'Expo', codemagic: 'Codemagic' };

/** "Flutter app", "Expo (React Native) app", but "iOS app (Swift)" as it is (its label already says app). */
export const appKindOf = (label) => (!label ? 'Mobile app' : /\bapp\b/i.test(label) ? label : `${label} app`);

export const VARIANT_LABEL = { debug: 'Debug', release: 'Release' };
export const VARIANT_HINT = { debug: 'for internal testing', release: 'for the stores' };

/** The provider whose account a build needs (`expo` | `codemagic`), or null for Flow's own builds. */
export const connectionOf = (provider) => (provider === 'eas' ? 'expo' : provider === 'codemagic' ? 'codemagic' : null);

export const CONNECTION_TITLE = { expo: 'Connect your Expo account', codemagic: 'Connect Codemagic' };
export const CONNECTION_NAME = { expo: 'Expo', codemagic: 'Codemagic' };

/** What a build of this kind gives you, one sentence. */
export function buildOutcome({ platform, variant, provider }) {
  if (platform === 'android') {
    return variant === 'release'
      ? 'An app bundle (.aab) for Google Play, signed with your upload key.'
      : 'An APK to install on a phone (scan a QR code), signed with a debug key.';
  }
  if (provider === 'mac') {
    return variant === 'release'
      ? 'An .ipa for the App Store, archived and signed on your Mac with its own Apple identity.'
      : 'An iOS build from your Mac: signed for your test iPhones when the Mac has a development identity, else a simulator app.';
  }
  if (provider === 'codemagic') {
    return variant === 'release' ? 'An .ipa for the App Store, built by your Codemagic workflow.' : 'A test build made by your Codemagic workflow.';
  }
  return variant === 'release'
    ? 'An .ipa for the App Store, signed with your Apple account through Expo.'
    : 'A build for your registered test iPhones (internal distribution).';
}

/**
 * The codemagic.yaml workflow a build takes when none is picked (the server's rule, vfs-service devicebuilds/codemagic.js):
 * same platform and kind, else same platform, else the only one without a platform; null when none fits.
 */
export function pickWorkflow(workflows, platform, variant) {
  const list = workflows || [];
  const exact = list.find((w) => w.platform === platform && w.variant === variant);
  if (exact) { return exact.id; }
  const samePlatform = list.filter((w) => w.platform === platform);
  if (samePlatform.length) { return (samePlatform.find((w) => !w.variant) || samePlatform[0]).id; }
  const neutral = list.filter((w) => !w.platform);
  if (neutral.length === 1) { return neutral[0].id; }
  return null;
}

export const STATUS = {
  queued: { label: 'Queued', color: 'default' },
  running: { label: 'Running', color: 'processing' },
  succeeded: { label: 'Succeeded', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  canceled: { label: 'Canceled', color: 'default' },
};

export const ARTIFACT_KIND = {
  apk: 'APK to install on a phone',
  aab: 'App bundle (.aab) for Google Play',
  ipa: 'iOS app (.ipa) for the App Store',
  app: 'iOS simulator app',
  other: 'File',
};

/** "45 s", "3 min 12 s", "1 h 4 min"; null without a duration. */
export function formatDuration(ms) {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) { return null; }
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) { return `${seconds} s`; }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) { return seconds % 60 ? `${minutes} min ${seconds % 60} s` : `${minutes} min`; }
  const hours = Math.floor(minutes / 60);
  return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
}

/** The duration of a build: its own, else since it started while it runs. */
export function buildDuration(build, now = Date.now()) {
  if (build?.durationMs !== null && build?.durationMs !== undefined) { return formatDuration(build.durationMs); }
  if (build?.status === 'running' && build.startedAt) { return formatDuration(now - Date.parse(build.startedAt)); }
  return null;
}

/** "1.4 MB", "820 KB"; null without a size. */
export function formatSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) { return null; }
  if (bytes < 1024 * 1024) { return `${Math.max(1, Math.round(bytes / 1024))} KB`; }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
